import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Typography,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
  FormHelperText,
  Divider
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ptBR from 'date-fns/locale/pt-BR';
import {
  Save as SaveIcon,
  Clear as ClearIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom'; // <-- IMPORTAÇÃO DO useLocation
import { useAuth } from '../../hooks/useAuth';

import { criarDefesa } from '../../services/defesaService';
import { listarMonografias } from '../../services/monografiaService';
import { listarPresidentes } from '../../services/presidenteService';
import { listarArguentes } from '../../services/arguenteService';
import { listarSupervisores } from '../../services/supervisorService';

function AgendarDefesaForm({ onSuccess }) {
  // Agora podemos usar useLocation
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const monografiaIdParam = searchParams.get('monografiaId'); // e.g. "?monografiaId=33"

  const navigate = useNavigate();
  const { user } = useAuth();

  // Estado inicial, levando em conta monografiaIdParam
  const [formData, setFormData] = useState({
    monografiaId: monografiaIdParam || '', // se tiver vindo da query, já coloca
    presidenteId: '',
    arguenteId: '',
    supervisorId: '',
    dataDefesa: null,
    local_defesa: '',
    status: 'AGENDADA'
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [monografias, setMonografias] = useState([]);
  const [presidentes, setPresidentes] = useState([]);
  const [arguentes, setArguentes] = useState([]);
  const [supervisores, setSupervisores] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Buscar monografias (se quiser filtrar por APROVADA, substitua o filtro)
        const monografiasRes = await listarMonografias();
        // Exemplo: filtrar monografias APROVADAS (ou remover filtro completamente)
        const monografiasFiltradas = monografiasRes.data.filter(
          (m) => m.status === 'APROVADA'
        );
        setMonografias(monografiasFiltradas);

        // Buscar presidentes, arguentes e supervisores
        const [presidentesRes, arguentesRes, supervisoresRes] = await Promise.all([
          listarPresidentes(),
          listarArguentes(),
          listarSupervisores()
        ]);
        setPresidentes(presidentesRes.data);
        setArguentes(arguentesRes.data);
        setSupervisores(supervisoresRes.data);
      } catch (err) {
        console.error('Erro ao carregar dados:', err);
        setError('Erro ao carregar dados necessários.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateChange = (newValue) => {
    setFormData((prev) => ({
      ...prev,
      dataDefesa: newValue
    }));
  };

  const resetForm = () => {
    setFormData({
      monografiaId: '',
      presidenteId: '',
      arguenteId: '',
      supervisorId: '',
      dataDefesa: null,
      localDefesa: '',
      status: 'AGENDADA'
    });
    setError(null);
  };

  const validarForm = () => {
    if (formData.presidenteId === formData.arguenteId) {
      setError('O presidente e o arguente não podem ser a mesma pessoa.');
      return false;
    }
    if (formData.presidenteId === formData.supervisorId) {
      setError('O presidente e o supervisor não podem ser a mesma pessoa.');
      return false;
    }
    if (formData.arguenteId === formData.supervisorId) {
      setError('O arguente e o supervisor não podem ser a mesma pessoa.');
      return false;
    }
    if (!formData.dataDefesa || new Date(formData.dataDefesa) < new Date()) {
      setError('A data da defesa não pode ser no passado.');
      return false;
    }
    if (!formData.monografiaId) {
      setError('É necessário selecionar uma monografia.');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!validarForm()) {
      setLoading(false);
      return;
    }

    try {
      const payload = {
        monografiaId: formData.monografiaId,
        presidenteId: formData.presidenteId,
        arguenteId: formData.arguenteId,
        supervisorId: formData.supervisorId,
        dataDefesa: formData.dataDefesa.toISOString(),
        local_defesa: formData.local_defesa, 
        status: formData.status
      };
      console.log("Dados enviados:", payload);
      await criarDefesa(payload);
      setSuccess(true);
      resetForm();
      if (onSuccess) onSuccess();
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Erro ao agendar defesa:', err);
      if (err.response) {
        console.error('Resposta de erro do servidor:', err.response.data);
      }
      setError(err.response?.data?.message || 'Erro ao agendar a defesa.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  // Permite a chefes_dept e direcao_cientifica
  const hasPermission =
    user.tipo === 'chefe_departamento' || user.tipo === 'direcao_cientifica';

  return (
    <Card>
      {!hasPermission ? (
        <CardContent>
          <Alert severity="warning">Você não possui permissão para agendar defesas.</Alert>
        </CardContent>
      ) : (
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Agendamento de Defesa
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Defesa agendada com sucesso! O projeto foi atualizado e a monografia estará
              disponível para avaliação.
            </Alert>
          )}

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
          >
            {/* Seleção de Monografia */}
            <FormControl fullWidth required>
              <InputLabel>Monografia</InputLabel>
              <Select
                name="monografiaId"
                value={formData.monografiaId}
                onChange={handleChange}
                label="Monografia"
              >
                {monografias.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    Versão {m.versao_documento} - {m.documento_monografia}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Selecione a monografia a ser defendida
              </FormHelperText>
            </FormControl>

            {/* Seleção de Presidente */}
            <FormControl fullWidth required>
              <InputLabel>Presidente do Júri</InputLabel>
              <Select
                name="presidenteId"
                value={formData.presidenteId}
                onChange={handleChange}
                label="Presidente do Júri"
              >
                {presidentes.map((presidente) => (
                  <MenuItem key={presidente.id} value={presidente.usuario_id}>
                    {presidente.usuario?.nome} - {presidente.departamento}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Seleção de Arguente */}
            <FormControl fullWidth required>
              <InputLabel>Arguente</InputLabel>
              <Select
                name="arguenteId"
                value={formData.arguenteId}
                onChange={handleChange}
                label="Arguente"
              >
                {arguentes.map((arguente) => (
                  <MenuItem key={arguente.id} value={arguente.usuario_id}>
                    {arguente.usuario?.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Seleção de Supervisor */}
            <FormControl fullWidth required>
              <InputLabel>Supervisor</InputLabel>
              <Select
                name="supervisorId"
                value={formData.supervisorId}
                onChange={handleChange}
                label="Supervisor"
              >
                {supervisores.map((supervisor) => (
                  <MenuItem key={supervisor.id} value={supervisor.id}>
                    {supervisor.Usuario?.nome} - {supervisor.area_especializacao}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>Selecione o supervisor</FormHelperText>
            </FormControl>

            {/* Data e Hora da Defesa */}
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
              <DateTimePicker
                label="Data e Hora da Defesa"
                value={formData.dataDefesa}
                onChange={handleDateChange}
                renderInput={(params) => <TextField {...params} fullWidth required />}
                minDate={new Date()}
                minutesStep={15}
              />
            </LocalizationProvider>

            {/* Local da Defesa */}
            <TextField
              fullWidth
              label="Local da Defesa"
            name="local_defesa"
              value={formData.localDefesa}
              onChange={handleChange}
              required
              placeholder="Ex: Sala de Defesas 1"
            />

            <Divider sx={{ my: 2 }} />

            {/* Botões de Ação */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
              <Button
                type="button"
                onClick={handleBack}
                disabled={loading}
                startIcon={<ArrowBackIcon />}
              >
                Voltar
              </Button>
              <Button
                type="button"
                onClick={resetForm}
                disabled={loading}
                startIcon={<ClearIcon />}
              >
                Limpar
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
              >
                {loading ? 'Agendando...' : 'Agendar Defesa'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      )}
    </Card>
  );
}

AgendarDefesaForm.propTypes = {
  onSuccess: PropTypes.func
};

export default AgendarDefesaForm;
